import _ from 'lodash';
//import moment from 'moment';
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

// material
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import SearchIcon from '@material-ui/icons/Search';
import DownloadIcon from '@material-ui/icons/CloudDownload';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import VisibilityIcon from '@material-ui/icons/Visibility';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { green } from '@material-ui/core/colors';

// ui
import FMPHeadline from '../../components/ui/FMPHeadline';
import SavedSearches from '../../components/SavedSearches/SavedSearches';
import FuelContractsContainer from '../../containers/inventory/FuelContracts';
import ResourcePopup from '../../components/Dialogs/ResourcePopup';
import PricingPopupIcon from '../../images/icons/pricingPopup.svg';
import documentIcon from '../../images/document.svg';

import { getSharedStyles } from '../../themes';
import PureEnhancedTable from '../../components/table/PureEnhancedTable';
import { allProducts, valueToDisplay } from '../../libs/products';
import ContractPricingPopup from '../../components/Dialogs/ContractPricingPopup';
import ContractSubContractsPopup from '../../components/Dialogs/ContractSubContractsPopup';
import FMPSectionHeader from '../../components/ui/FMPSectionHeader';
import FMPTerminalMultiSelect from '../../components/pureForm/FMPTerminalMultiSelect';
import FMPSupplierMultiSelect from '../../components/pureForm/FMPSupplierMultiSelect';
import FMPMonthYearPicker from '../../components/pureForm/FMPMonthYearPicker';

// data
import FormComponent from '../../components/FormComponent';
import SuppliersContainer from '../../containers/logistics/Suppliers';
import TerminalsContainer from '../../containers/logistics/Terminals';
import { post } from '../../modules/utility';
import FMPProductSelect from '../../components/pureForm/FMPProductSelect';

const styles = (theme) => ({
	...getSharedStyles(theme),
	button: {
		verticalAlign: 'middle',
		padding: '8px 10px',
	},
	pricingIcon: {
		marginRight: 12,
		display: 'inline-block',
		width: 34,
		height: 31,
		backgroundImage: `url('${PricingPopupIcon}')`,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		position: 'relative',
		flexShrink: 0,
		cursor: 'pointer',
	},
	documentIcon: {
		marginRight: 12,
		display: 'inline-block',
		width: 34,
		height: 40,
		backgroundImage: `url('${documentIcon}')`,
		backgroundPosition: 'center',
		backgroundSize: 'cover',
		verticalAlign: 'middle',
		position: 'relative',
		flexShrink: 0,
		cursor: 'pointer',
	},
	docContainer: {
		position: 'absolute',
	},
	missingCost: {
		fontWeight: 900,
		color: '#FF500F',
	},
	dontNeedCostYet: {
		color: '#888',
		fontWeight: 300,
	},
	hasCost: {
		fontWeight: 900,
		color: '#4CAF50',
	},
	dateRange: {
		marginRight: 10,
	},
	commentDateTime: {
		fontSize: '80%',
		color: '#666',
	},
	bigButton: {
		borderRadius: '100px',
		padding: '18px 34px',
		fontSize: 16,
	},
	smallButton: {
		borderRadius: '100px',
		fontWeight: 200,
	},
	snackbarSuccess: {
		backgroundColor: green[600],
		display: 'flex',
		alignItems: 'center',
	},
	snackbarIcon: {
		marginRight: 8,
		fontSize: 20,
	},
	pickPlease: {
		background: '#F7F7F7',
		border: '1px solid #DDDDDD',
		padding: 20,
		color: '#666',
	},
});

const currentColumns = [
	{
		sortId: 'fuelContractId',
		displayId: 'idDisplay',
		label: 'Contract #s',
	},
	{
		sortId: 'sourceCustomer',
		displayId: 'contractDisplay',
		label: 'Fuel Vendor',
	},
	{
		sortId: 'subContractCount',
		displayId: 'subContractsDisplay',
		label: 'Sub Contracts',
		isNotSortable: true,
	},
	{
		sortId: 'terminalDisplay',
		displayId: 'allocationDisplay',
		label: 'Allocation',
	},
	{
		sortId: 'numberOfGallons',
		displayId: 'gallonDisplay',
		label: 'Gallons / Barrels',
	},
	{
		sortId: 'railcarsSort',
		displayId: 'railcarDisplay',
		label: 'Railcars',
	},
	{
		sortId: 'quarter',
		displayId: 'quarterDisplay',
		labels: ['Qtr', 'Period'],
	},
	{
		sortId: 'startDate',
		displayId: 'startEndDateDisplay',
		labels: ['Start', 'End'],
	},
	{
		sortId: 'costSort',
		displayId: 'pricingDisplay',
		label: 'Pricing',
	},
	{
		sortId: 'pricing',
		displayId: 'costsDisplay',
		label: 'Costs',
	},
	{
		sortId: 'documentInfo',
		displayId: 'documentDisplay',
		label: 'Docs',
	},
	{
		sortId: 'edit',
		displayId: 'editButtons',
		label: 'Edit',
		style: { textAlign: 'center' },
		isNotSortable: true,
	},
];

function TabContainer({ children, dir }) {
	return (
		<Typography component="div" dir={dir}>
			{children}
		</Typography>
	);
}

TabContainer.propTypes = {
	children: PropTypes.node.isRequired,
	dir: PropTypes.string.isRequired,
};

const isLocalhost = Boolean(window.location.hostname === 'localhost');

class FuelContracts extends FormComponent {
	constructor(props) {
		super(props);

		const dateRange = 'lastMonth';
		const { yearMonth } = this.dateRangeToDates(dateRange);
		this.state = {
			filterMap: {},
			snackbarOpen: false,
			snackbarMessage: '',
			isViewDialogOpen: false,
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
			isPricingPoppedUp: false,
			fuelContractPoppedUp: undefined,

			isSubContractsPoppedUp: false,
			subContractsPoppedUp: [],
			subContractsFuelContractId: undefined,
			isSubContractsLoading: false,

			upcomingRows: undefined,
			currentRows: undefined,
			completedRows: undefined,
			productType: undefined,

			formFieldError: {},
			deliveryType: undefined,
			invoiceType: undefined,
			dateRange,
			yearMonth,
			yearMonthEnd: yearMonth,
			isSearching: false,
			supplierIds: [],
			fuelVendorSupplierIds: [],
			terminalIds: [],
		};
	}

	componentDidMount() {
		const { terminals, searchTerminals, suppliers, searchSuppliers } = this.props;

		if (_.isEmpty(suppliers)) {
			searchSuppliers();
		}
		if (_.isEmpty(terminals)) {
			searchTerminals();
		}
	}

	formatFuelContracts = (apiSearchResult) => {
		const { collections } = apiSearchResult;

		const { fuelContractCollection, supplierCollection, terminalCollection, fuelSubContractCountCollection } = collections;
		const subContractCountMap = _.keyBy(fuelSubContractCountCollection || [], 'fuelContractId');

		// Use this to filter
		const { yearMonth, yearMonthEnd, terminalIds, supplierIds, fuelVendorSupplierIds } = this.state;
		const filteredProductType = this.state.productType;

		let startOfMonth, endOfMonth;
		if (yearMonth) {
			const yearMonthMoment = moment(yearMonth, 'YYYY-MM');
			console.log('yearMonthMoment', yearMonthMoment.format('YYYY-MM-DD'));
			startOfMonth = moment(yearMonthMoment).startOf('month');
			endOfMonth = moment(yearMonthMoment).endOf('month');
		}

		if (yearMonthEnd) {
			const yearMonthEndMoment = moment(yearMonthEnd, 'YYYY-MM');
			console.log('yearMonthEndMoment', yearMonthEndMoment.format('YYYY-MM-DD'));
			endOfMonth = moment(yearMonthEndMoment).endOf('month');
		}

		const { classes } = this.props;
		//- get the quarters
		// const startOfCurrentQuarter = moment().startOf('quarter');
		// const endOfCurrentQuarter = moment().endOf('quarter');
		const now = moment();

		const fuelContracts = fuelContractCollection;
		const supplierMap = _.keyBy(supplierCollection, 'supplierId');
		const terminalMap = _.keyBy(terminalCollection, 'terminalId');

		const filteredFuelContracts = [];
		fuelContracts.forEach((fuelContract) => {
			const {
				fuelContractId,
				allocatedForTerminalId,
				supplierForTerminalId,
				fuelVendorSupplierId,
				sourceCustomer,
				contractNumber,
				quarter,
				timePeriod,
				numberOfGallons,
				numberOfBarrels,
				numberOfRailcars,
				resourceIds,
				productType,
				finalCalculatedCosts,
				contractLoadingNumber,
				startDate,
				endDate,
			} = fuelContract;

			// Filter
			if (!_.isEmpty(supplierIds)) {
				if (!_.includes(supplierIds, supplierForTerminalId)) {
					return;
				}
			}

			if (!_.isEmpty(fuelVendorSupplierIds)) {
				if (!_.includes(fuelVendorSupplierIds, fuelVendorSupplierId)) {
					return;
				}
			}

			if (!_.isEmpty(terminalIds)) {
				if (!_.includes(terminalIds, allocatedForTerminalId)) {
					return;
				}
			}
			if (filteredProductType) {
				if (filteredProductType !== productType) {
					return;
				}
			}
			if (startOfMonth) {
				const contractStart = moment(startDate);
				const contractEnd = moment(endDate);
				// const isStartIn = contractStart.isBetween(startOfMonth, endOfMonth, null, '[]');
				// const isEndIn = contractEnd.isBetween(startOfMonth, endOfMonth, null, '[]');
				//if (!isStartIn || !isEndIn) {
				//	return;
				//}

				const isAnyOverlap =
					contractStart.isBefore(endOfMonth) && contractEnd.isAfter(startOfMonth);
				if (!isAnyOverlap) {
					return;
				}
			}

			const supplier = supplierMap[supplierForTerminalId] || { name: 'Missing' };
			const terminal = terminalMap[allocatedForTerminalId] || { name: 'Missing' };

			const fuelDisplay = valueToDisplay(productType);

			fuelContract.key = fuelContractId;
			fuelContract.idDisplay = (
				<div>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<span style={{ color: '#555' }}>FMP:</span>&nbsp;{fuelContractId}
						<Tooltip title="Copy FMP Contract ID">
							<IconButton
								size="small"
								style={{ padding: 2, marginLeft: 4 }}
								onClick={(e) => {
									e.stopPropagation();
									navigator.clipboard.writeText(String(fuelContractId));
									this.setState({
										snackbarOpen: true,
										snackbarMessage: `${fuelContractId} copied to clipboard`,
									});
								}}
							>
								<FileCopyIcon style={{ fontSize: 13 }} />
							</IconButton>
						</Tooltip>
					</div>
					<div><span style={{ color: '#555' }}>Pearson:</span> {contractNumber || '-'}</div>
				</div>
			);
			const fuelVendorSupplier = fuelVendorSupplierId ? supplierMap[fuelVendorSupplierId] : null;
			fuelContract.contractDisplay = (
				<div>
					<div>Fuel Vendor : {fuelVendorSupplier ? fuelVendorSupplier.name : '-'}</div>
					<div>Source : {sourceCustomer}</div>
					{contractLoadingNumber && <div>Loading #:{contractLoadingNumber}</div>}
				</div>
			);

			const subContractCountEntry = subContractCountMap[fuelContractId];
			const subContractCount = subContractCountEntry ? subContractCountEntry.subContractCount : 0;
			fuelContract.subContractCount = subContractCount;
			fuelContract.subContractsDisplay = (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<span
						style={{
							cursor: subContractCount > 0 ? 'pointer' : 'default',
							color: subContractCount > 0 ? '#1976d2' : '#888',
							fontWeight: subContractCount > 0 ? 600 : 400,
							fontSize: 16,
						}}
						onClick={
							subContractCount > 0
								? () => this.handleSubContractsClick({ fuelContract })
								: undefined
						}
					>
						{subContractCount}
					</span>
					{subContractCount > 0 && (
						<Tooltip title="View Sub Contracts">
							<IconButton
								size="small"
								style={{ padding: 2, marginLeft: 4 }}
								onClick={(e) => {
									e.stopPropagation();
									this.handleSubContractsClick({ fuelContract });
								}}
							>
								<VisibilityIcon style={{ fontSize: 18 }} />
							</IconButton>
						</Tooltip>
					)}
				</div>
			);

			fuelContract.contractNumbersDisplay = (
				<div>
					<div><span style={{ color: '#555' }}>Pearson:</span> {contractNumber || '-'}</div>
				</div>
			);

			fuelContract.allocationDisplay = (
				<div style={{ fontSize: '90%' }}>
					<div>Fuel : {fuelDisplay}</div>
					<div>Terminal : {terminal.name}</div>
					<div>Supplier : {supplier.name}</div>
				</div>
			);
			// for filter
			fuelContract.terminalName = terminal.name;
			fuelContract.supplierName = supplier.name;

			fuelContract.gallonDisplay = (
				<div>
					<div>{numberOfGallons.toLocaleString()} contracted</div>
					<div style={{ fontSize: '80%', color: '#777' }}>
						{_.isNil(numberOfBarrels) ? '(-)' : `(${numberOfBarrels})`}
					</div>
				</div>
			);

			fuelContract.railcarsSort = _.isFinite(numberOfRailcars) ? numberOfRailcars : -1;
			fuelContract.railcarDisplay = (
				<div>
					<div>{_.isNil(numberOfRailcars) ? '-' : numberOfRailcars}</div>
				</div>
			);

			fuelContract.quarterDisplay = (
				<div>
					<div>{quarter}</div>
					<div>{_.capitalize(timePeriod)}</div>
				</div>
			);

			fuelContract.startEndDateDisplay = (
				<div>
					<div>{moment(startDate).format('M/D/YYYY')}</div>
					<div>{moment(endDate).format('M/D/YYYY')}</div>
				</div>
			);

			//- finalCalculatedCosts. Here go through and see which costs we need

			//let numberOfCompleted = 0;
			fuelContract.costsDisplay = finalCalculatedCosts.map((finalCalculatedCost, i) => {
				const { startDate, endDate, cost, bulkCost } = finalCalculatedCost;
				const endMoment = moment(endDate).endOf('day');
				const month = moment(startDate).format('MMM');
				let shouldBeDone = endMoment.isBefore(now);

				if (shouldBeDone) {
					if (cost && parseFloat(cost)) {
						return (
							<div key={i}>
								<span className={classes.hasCost}>{month}</span> : <strong>{cost}</strong>
								{bulkCost && (
									<span>
										<span style={{ color: '#aaa', paddingLeft: 7, paddingRight: 7 }}> | </span>
										<strong>{bulkCost}</strong>
									</span>
								)}
							</div>
						);
					} else {
						return (
							<div key={i}>
								<span className={classes.missingCost}>{month}</span> : <span>????</span>
							</div>
						);
					}
				} else {
					return (
						<div key={i} className={classes.dontNeedCostYet}>
							<span className={classes.dontNeedCostYet}>{month}</span> :{' '}
							<span>{cost || 'n/a'}</span>
						</div>
					);
				}
			});

			fuelContract.pricingDisplay = (
				<div>
					<div
						onClick={() => {
							this.handlePricingClick({ fuelContract });
						}}
						className={classes.pricingIcon}
					/>
				</div>
			);

			if (_.isEmpty(resourceIds)) {
				fuelContract.documentDisplay = '-';
			} else {
				let left = 0;
				let zIndex = 100;
				fuelContract.documentDisplay = (
					<div
						onClick={() => {
							this.handleResourcesClick({ resourceIds });
						}}
						style={{ position: 'relative', height: 40, width: 40 }}
					>
						{resourceIds.map((resourceId, i) => {
							left = i * 10;
							zIndex--;
							return (
								<div className={classes.docContainer} style={{ left, zIndex }} key={resourceId}>
									<div className={classes.documentIcon} />
								</div>
							);
						})}
					</div>
				);
			}

			//const quarterMoment = moment(quarter, 'YYYY - Q').startOf('quarter');
			fuelContract.editButtons = (
				<Button
					size="small"
					className={classes.button}
					onClick={() => this.handleEdit(fuelContractId)}
				>
					Edit
				</Button>
			);

			filteredFuelContracts.push(fuelContract);
		});
		this.setState({ filteredFuelContracts });
	};

	handleSearch = (event) => {
		this.setState({ search: event.target.value });
	};

	handleEdit = (fuelContractId) => {
		this.props.history.push(`/inventoryManagement/fuelContract/update/${fuelContractId}`);
	};

	handleRowClick = (fuelContractId) => {
		const { fuelContracts } = this.props;
		const viewFuelContract = _.find(fuelContracts, { fuelContractId });
		this.setState({
			viewFuelContract,
			isViewDialogOpen: true,
		});
	};

	handleCloseViewDialog = () => {
		this.setState({
			isViewDialogOpen: false,
			viewFuelContract: undefined,
		});
	};

	handleCloseResourcePopup = () => {
		this.setState({
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
		});
	};

	handleResourcesClick = ({ resourceIds }) => {
		this.setState({
			isResourcePopupOpen: true,
			resourceIdsPoppedUp: resourceIds,
		});
	};

	handlePricingClick = ({ fuelContract }) => {
		this.setState({
			isPricingPoppedUp: true,
			fuelContractPoppedUp: fuelContract,
		});
	};

	handleClosePricingPopup = () => {
		this.setState({
			isPricingPoppedUp: false,
			fuelContractPoppedUp: undefined,
		});
	};

	handleSubContractsClick = ({ fuelContract }) => {
		const { showErrors } = this.props;
		this.setState({
			isSubContractsPoppedUp: true,
			subContractsPoppedUp: [],
			subContractsFuelContractId: fuelContract.fuelContractId,
			isSubContractsLoading: true,
		});
		post({
			method: 'fuelSubContract.search',
			params: { parentFuelContractId: fuelContract.fuelContractId },
			callback: (err, result) => {
				if (err) {
					this.setState({ isSubContractsLoading: false });
					return showErrors(_.isArray(err) ? err : [err]);
				}
				const subContracts = _.get(result, ['collections', 'fuelSubContractCollection'], []);
				this.setState({ subContractsPoppedUp: subContracts, isSubContractsLoading: false });
			},
		});
	};

	handleCloseSubContractsPopup = () => {
		this.setState({
			isSubContractsPoppedUp: false,
			subContractsPoppedUp: [],
			subContractsFuelContractId: undefined,
		});
	};

	handleTabChange = (event, tabIndex) => {
		this.setState({ tabIndex });
	};

	handleChangeTabIndex = (index) => {
		this.setState({ tabIndex: index });
	};

	render() {
		const { canRwContracts } = this.props;

		if (!canRwContracts) {
			return <div>Permission denied</div>;
		}

		const { classes } = this.props;
		const { snackbarOpen, snackbarMessage } = this.state;

		return (
			<>
				<Grid container spacing={2}>
					<Grid item md={8} xs={12}>
						{this.renderSearchOptions()}
					</Grid>
					<Grid item md={4} xs={12}>
						<SavedSearches
							searchContext="fuelContracts"
							onSubmitSearch={this.onSubmitSavedSearch}
							getStateToSave={this.getStateToSave}
						/>
					</Grid>
					<Grid item md={12} xs={12}>
						{this.renderResults()}
					</Grid>
				</Grid>
				<Snackbar
					anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
					open={snackbarOpen}
					autoHideDuration={2500}
					onClose={() => this.setState({ snackbarOpen: false })}
				>
					<SnackbarContent
						className={classes.snackbarSuccess}
						message={
							<span style={{ display: 'flex', alignItems: 'center' }}>
								<CheckCircleIcon className={classes.snackbarIcon} />
								{snackbarMessage}
							</span>
						}
					/>
				</Snackbar>
			</>
		);
	}

	onSubmitSavedSearch = ({ savedState }) => {
		//- Older saved searches predate the fuel vendor filter; reset it so it doesn't stick
		if (!savedState.fuelVendorSupplierIds) {
			savedState.fuelVendorSupplierIds = [];
		}

		if (savedState.dateRange) {
			if (savedState.dateRange !== 'custom' || !savedState.yearMonth) {
				const { yearMonth } = this.dateRangeToDates(savedState.dateRange);
				if (yearMonth) {
					savedState.yearMonth = yearMonth;
				}
			}
		}

		console.log(savedState);
		this.setState(savedState, () => {
			this.submitContractSearch();
		});
	};

	submitContractSearch = () => {
		const { showErrors } = this.props;

		const formFieldError = {};
		_.values(this.requiredPathMap).forEach(({ path, label }) => {
			if (!this.state[path]) {
				formFieldError[path] = `${label} is required`;
			}
		});

		const { yearMonth, yearMonthEnd } = this.state;
		if (yearMonth && yearMonthEnd) {
			if (moment(yearMonthEnd).isBefore(moment(yearMonth))) {
				formFieldError['yearMonth'] = `Hey time traveler, start can't be before the end month.`;
				formFieldError['yearMonthEnd'] = `Hey time traveler, start can't be before the end month.`;
			}
		}

		//- Get all the values and do individual checks
		if (_.keys(formFieldError).length) {
			this.setState({ formFieldError });
			showErrors(_.values(formFieldError));
			return;
		}

		this.setState({
			isSearching: true,
		});

		post({
			method: 'fuelContract.search',
			params: {
				afterDate: '2023-10-18',
			},
			callback: (err, apiSearchResult) => {
				this.setState({ isSearching: false });
				if (err) {
					return showErrors(_.isArray(err) ? err : [err]);
				}
				this.formatFuelContracts(apiSearchResult);
			},
		});
	};

	getApiParams = () => {
		const { accessToken } = this.props;
		const { yearMonth } = this.state;
		const params = { accessToken };
		if (yearMonth) {
			params.afterDate = moment(yearMonth, 'YYYY-MM').startOf('month').format('YYYY-MM-DD');
		}
		return params;
	};

	getStateToSave = () => {
		const {
			terminalIds,
			supplierIds,
			fuelVendorSupplierIds,
			dateRange,
			productType,
			yearMonth,
			yearMonthEnd,
		} = this.state;

		const formFieldError = {};
		_.values(this.requiredPathMap).forEach(({ path, label }) => {
			if (!this.state[path]) {
				formFieldError[path] = `${label} is required`;
			}
		});

		//- Get all the values and do individual checks
		if (_.keys(formFieldError).length) {
			this.setState({ formFieldError });
			this.props.showErrors(_.values(formFieldError));
			return null;
		}

		const savedState = { terminalIds, supplierIds, fuelVendorSupplierIds, dateRange, productType };

		if (dateRange === 'custom') {
			savedState.yearMonth = yearMonth;
			savedState.yearMonthEnd = yearMonthEnd;
		}

		return savedState;
	};

	renderSearchOptions = () => {
		const { classes, canRwContracts, suppliers, terminals } = this.props;
		return (
			<>
				<FMPHeadline
					label="Fuel Contracts"
					buttonText="ADD NEW CONTRACT"
					url="/inventoryManagement/fuelContract/add"
					canMakeUpdates={canRwContracts}
				/>
				<Paper className={classes.paper}>
					<Grid container spacing={2}>
						{this.renderDateRangeOptions()}

						<FMPSupplierMultiSelect
							{...this.setForm('fuelVendorSupplierIds', 'Fuel Vendors', false, [])}
							suppliers={suppliers}
						/>

						<FMPSupplierMultiSelect
							{...this.setForm('supplierIds', 'Suppliers', false, [])}
							suppliers={suppliers}
						/>

						<FMPTerminalMultiSelect
							{...this.setForm('terminalIds', 'Terminals', false, [])}
							terminals={terminals}
						/>

						<FMPProductSelect
							{...this.setForm(`productType`, 'Product', false)}
							productTypes={_.map(allProducts, ({ value }) => {
								return value;
							})}
						/>

						<Grid item>{this.renderSubmitButtons()}</Grid>
					</Grid>
				</Paper>
			</>
		);
	};

	renderDateButton = (label, range) => {
		const { classes } = this.props;
		const { dateRange } = this.state;

		const variant = dateRange === range ? 'contained' : 'outlined';

		return (
			<Button
				className={classes.dateRange}
				variant={variant}
				color="primary"
				onClick={() => {
					this.setDateRange(range);
				}}
			>
				{label}
			</Button>
		);
	};

	setDateRange = (dateRange) => {
		const { yearMonth } = this.dateRangeToDates(dateRange);
		if (!yearMonth) {
			this.setState({ dateRange });
		} else {
			this.setState({ dateRange, yearMonth, yearMonthEnd: yearMonth });
		}
	};
	dateRangeToDates = (dateRange) => {
		if (dateRange === 'thisMonth') {
			return {
				yearMonth: moment().startOf('month').format('YYYY-MM'),
			};
		} else if (dateRange === 'lastMonth') {
			return {
				yearMonth: moment().subtract(1, 'month').startOf('month').format('YYYY-MM'),
			};
		} else {
			return {};
		}
	};

	renderDateRangeOptions = () => {
		const { dateRange } = this.state;
		let disabled = true;
		const isCustomRange = dateRange === 'custom';

		if (isCustomRange) {
			disabled = false;
		}

		return (
			<>
				<Grid item md={12}>
					<FMPSectionHeader label={'Date Range'} isTop={true} />
				</Grid>

				<Grid item md={12} style={{ display: 'flex' }}>
					{this.renderDateButton('Current Month', 'thisMonth')}
					{this.renderDateButton('Last Month', 'lastMonth')}
					{this.renderDateButton('Custom', 'custom')}
				</Grid>

				<FMPMonthYearPicker
					{...this.setForm('yearMonth', isCustomRange ? 'Start Month / Year' : 'Month / Year')}
					disabled={disabled}
					showFuture={true}
					md={6}
				/>

				{isCustomRange && (
					<FMPMonthYearPicker
						{...this.setForm('yearMonthEnd', 'End Month / Year')}
						disabled={disabled}
						showFuture={true}
						md={6}
					/>
				)}
			</>
		);
	};

	renderSubmitButtons = () => {
		const { classes } = this.props;
		const { isSearching } = this.state;

		return (
			<Grid container spacing={2}>
				<Grid item md={12} style={{ paddingTop: 25, display: 'flex' }}>
					<Button
						className={classes.bigButton}
						variant="contained"
						color="primary"
						onClick={this.submitContractSearch}
						disabled={isSearching}
					>
						<SearchIcon className={classes.icon} />
						Fuel Contract Search
					</Button>

					{(() => {
						const params = this.getApiParams();
						params.downloadCsv = true;
						const request = { method: 'fuelContract.search', params };
						let host = '';
						if (isLocalhost) {
							host = 'http://localhost:3700';
						}
						const downloadUrl = `${host}/api?request=${JSON.stringify(request)}`;
						return (
							<a
								href={downloadUrl}
								style={{ textDecoration: 'none', paddingLeft: 10 }}
								target="_blank"
								rel="noreferrer noopener"
							>
								<Button
									className={classes.bigButton}
									variant="contained"
									color="primary"
									disabled={isSearching}
								>
									<DownloadIcon className={classes.icon} />
									&nbsp;Download Fuel Contracts
								</Button>
							</a>
						);
					})()}

					{isSearching && (
						<div className={classes.runningSearch}>
							<CircularProgress />
							<span style={{ paddingLeft: 5 }}>Searching...</span>
						</div>
					)}
				</Grid>
			</Grid>
		);
	};

	renderResults() {
		const { classes, resourceMap } = this.props;
		const { isSearching } = this.state;
		const { filteredFuelContracts } = this.state;

		if (!filteredFuelContracts) {
			return (
				<div style={{ color: 'gray', fontStyle: 'italic' }}>Click search to see contracts</div>
			);
		}

		if (isSearching) {
			return (
				<div className={classes.runningSearch}>
					<CircularProgress />
					<span style={{ paddingLeft: 5 }}>Searching...</span>
				</div>
			);
		}

		return (
			<>
				<Paper className={classes.paper}>
					<PureEnhancedTable
						columns={currentColumns}
						rows={filteredFuelContracts}
						filterId={'fuelContractId'}
						defaultOrder={'asc'}
						defaultOrderBy={'sourceCustomer'}
						isDense={true}
					/>

					<ResourcePopup
						open={this.state.isResourcePopupOpen}
						onClose={this.handleCloseResourcePopup}
						resourceIds={this.state.resourceIdsPoppedUp}
						resourceMap={resourceMap}
					/>

					<ContractPricingPopup
						open={this.state.isPricingPoppedUp}
						onClose={this.handleClosePricingPopup}
						fuelContract={this.state.fuelContractPoppedUp}
					/>

					<ContractSubContractsPopup
						open={this.state.isSubContractsPoppedUp}
						onClose={this.handleCloseSubContractsPopup}
						fuelContractId={this.state.subContractsFuelContractId}
						subContracts={this.state.subContractsPoppedUp}
						isLoading={this.state.isSubContractsLoading}
					/>
				</Paper>
			</>
		);
	}
}

FuelContracts.propTypes = {
	classes: PropTypes.object.isRequired,
	searchFuelContracts: PropTypes.func.isRequired,
	fuelContracts: PropTypes.array,

	showErrors: PropTypes.func.isRequired,

	supplierMap: PropTypes.object,
	terminalMap: PropTypes.object,
	resourceMap: PropTypes.object,

	history: PropTypes.object,
	theme: PropTypes.object.isRequired,

	canRwContracts: PropTypes.bool,

	terminals: PropTypes.array,
	searchTerminals: PropTypes.func.isRequired,
	suppliers: PropTypes.array,
	searchSuppliers: PropTypes.func.isRequired,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	SuppliersContainer,
	TerminalsContainer,
	FuelContractsContainer,
])(FuelContracts);
